/**
 * Shown in place of an endless spinner when a screen's data isn't cached and the
 * device is offline (the underlying query is paused with no data). Reusable in any
 * loading branch: render this instead of the spinner when offline && !data.
 */
import { WifiOff } from 'lucide-react'

interface OfflineFallbackProps {
  onRetry?: () => void
  onBack?: () => void
  message?: string
}

export function OfflineFallback({ onRetry, onBack, message }: OfflineFallbackProps) {
  return (
    <div className="flex flex-col items-center justify-center gap-4 p-8 text-center">
      <WifiOff className="h-12 w-12 text-secondary-400" />
      <div>
        <p className="text-lg font-semibold text-secondary-900">No internet connection</p>
        <p className="mt-1 max-w-xs text-sm text-secondary-600">
          {message ?? "This content isn't saved for offline use. Reconnect to load it."}
        </p>
      </div>
      <div className="flex gap-3">
        {onBack && (
          <button
            onClick={onBack}
            className="rounded-lg bg-secondary-100 px-4 py-2 text-sm font-medium text-secondary-700 transition-colors hover:bg-secondary-200"
          >
            Back
          </button>
        )}
        {onRetry && (
          <button
            onClick={onRetry}
            className="rounded-lg bg-primary-500 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-primary-600"
          >
            Refresh
          </button>
        )}
      </div>
    </div>
  )
}
